/**
 * AudioManager — Web Audio API. All SFX are synthesized at runtime (original,
 * CC0 by construction — no sampled assets to license). Three gain buses:
 * master → {sfx, music}. Volumes persist per browser.
 *
 * Positional gunshots/footsteps use a PannerNode placed relative to the
 * listener (the local player's camera).
 */

const VOL_KEY = "neonpurge.volumes";
const SVOL_KEY = "neonpurge.soundvols";

/** DEFAULT per-sound levels — hand-calibrated in-game (supremechibre's mixer
 *  session, 2026-07-10). A user's own slider (soundVols) overrides these. */
const SOUND_DEFAULTS: Record<string, number> = {
  die: 0.5, uefo: 1.15, wind: 0.8, hurt2: 0.8, knife: 0.65, reload: 0.85,
  gunshot: 0.55, zombie1: 0.85, zombie2: 0.9, zombie3: 0.8, bombdrop: 1.05,
  drifting: 0.9, footstep: 1.35, imperion: 0.9, explosion: 0.85,
  voice_won: 1.15, helicopter: 1.3, killConfirm: 0.85, voice_sologame: 1.4,
  carbacktothefutur: 1.25, voice_backtothefuture: 1.2,
  voice_standard: 1.45, voice_moon: 1.45, voice_blitz: 1.45,
  voice_glass_cannon: 1.45, voice_burnout: 1.45, voice_flags: 1.45,
  voice_treasure: 1.45, voice_treasure_loose: 1.45, voice_treasure_stolen: 1.45,
  voice_flags_red_stole: 1.45, voice_flags_blue_stole: 1.45,
  voice_flags_red_point: 1.45, voice_flags_blue_point: 1.45,
  voice_map_rooftop_spiderweb: 1.45,
  voice_relay: 1.45, voice_onegun: 1.45, voice_horde: 1.45,
  voice_royale: 1.45, voice_map_mega_chibre: 1.45,
  voice_sprinter: 1.45, voice_gunslinger: 1.45, voice_vanguard: 1.45,
  voice_grenadier: 1.45, voice_body_double: 1.45, voice_goblin_loot: 1.45,
  voice_penisgun: 1.45,
  voice_watergun: 1.45, water: 1.2,
  voice_rocket_chest: 1.45, voice_gun_taken: 1.45, voice_gun_swap: 1.45,
  voice_skull_taken: 1.45, voice_skull_loose: 1.45,
  wormvomit: 1.25,
  // FART BLASTER hazard: comedy must be HEARD, but not drown gunfire
  fart1: 1.1, fart2: 1.1, fart3: 1.1, fart4: 1.1, fart5: 1.1, fart6: 1.1, fart7: 1.1,
  throwup: 1.15,
  voice_fartblaster: 1.45,
  horseriding: 0.6, // a continuous loop — keep it gentle under the action
  bubble: 0.85,      // plays on every dive — keep it under the action
  bossdamaged: 0.9,
};

/** Per-sound volume multipliers (0..2, default 1) keyed by sample name. */
function loadSoundVols(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SVOL_KEY);
    if (raw) return JSON.parse(raw) as Record<string, number>;
  } catch { /* defaults */ }
  return {};
}

interface Volumes {
  master: number;
  sfx: number;
  music: number;
  /** Announcer-vocals bus (all voice_* lines). */
  voice: number;
}

function loadVolumes(): Volumes {
  try {
    const raw = localStorage.getItem(VOL_KEY);
    if (raw) return { master: 0.8, sfx: 0.9, music: 0.5, voice: 1, ...(JSON.parse(raw) as Partial<Volumes>) };
  } catch {
    /* defaults */
  }
  return { master: 0.8, sfx: 0.9, music: 0.5, voice: 1 };
}

/** Music tracks (streamed via HTML Audio, routed through the music bus).
 *  "maps" is a virtual request that resolves to one of the real in-match
 *  tracks at random each time a match starts (maps / maps2). */
export type MusicTrack =
  | "menu" | "maps" | "maps2" | "maps3" | "maps4"
  | "western1" | "western2" | "skyhaven1" | "skyhaven2"
  | "medieval1" | "medieval2";
const MUSIC_URLS: Record<MusicTrack, string> = {
  menu: "/audio/music/menu.mp3",
  maps: "/audio/music/maps.mp3",
  maps2: "/audio/music/maps2.mp3",
  maps3: "/audio/music/maps3.mp3",
  maps4: "/audio/music/maps4.mp3",
  western1: "/audio/music/western1.mp3",
  western2: "/audio/music/western2.mp3",
  skyhaven1: "/audio/music/skyhaven1.mp3",
  skyhaven2: "/audio/music/skyhaven2.mp3",
  medieval1: "/audio/music/medieval1.mp3",
  medieval2: "/audio/music/medieval2.mp3",
};
/** Real map tracks picked from when "maps" is requested. */
const MAP_TRACKS: MusicTrack[] = ["maps", "maps2", "maps3", "maps4"];
/** Maps with their own music pool instead of the generic set. */
const MAP_MUSIC: Record<string, MusicTrack[]> = {
  wild_west_train_pursuit: ["western1", "western2"],
  neo_island: ["skyhaven1", "skyhaven2"],
  rooftop_spiderweb: ["skyhaven1", "skyhaven2"],
  medieval_castle: ["medieval1", "medieval2"],
};
/** Every track that counts as "in-match background" (don't restart mid-match). */
const IN_MATCH_TRACKS = new Set<MusicTrack>([
  ...MAP_TRACKS, "western1", "western2", "skyhaven1", "skyhaven2", "medieval1", "medieval2",
]);
const SFX_URLS = {
  gunshot: "/audio/sfx/gunshot.m4a",
  reload: "/audio/sfx/gunrecharge.m4a",
  hurt: "/audio/sfx/hit.m4a",
  die: "/audio/sfx/die.m4a",
  footstep: "/audio/sfx/footstep.m4a",
  explosion: "/audio/sfx/explosion.m4a",
  killConfirm: "/audio/sfx/die-opponent.m4a",
  knife: "/audio/sfx/knife.m4a",
  wind: "/audio/sfx/wind.m4a",
  hurt2: "/audio/sfx/hit2.m4a",
  bombdrop: "/audio/sfx/bombdrop.m4a",
  imperion: "/audio/sfx/imperion.m4a",
  drifting: "/audio/sfx/drifting.m4a", // arena drift screech (recorded)
  zombie1: "/audio/sfx/zombie1.m4a",
  zombie2: "/audio/sfx/zombie2.m4a",
  zombie3: "/audio/sfx/zombie3.m4a",
  goaway: "/audio/sfx/goaway.m4a",
  taunt1: "/audio/sfx/taunt1.m4a",
  taunt2: "/audio/sfx/taunt2.m4a",
  taunt3: "/audio/sfx/taunt3.m4a",
  taunt4: "/audio/sfx/taunt4.m4a",
  // voice announcer (recorded)
  voice_gungame: "/audio/voice/gungame.m4a",
  voice_deathmatch: "/audio/voice/deathmatch.m4a",
  voice_arena: "/audio/voice/arena.m4a",
  voice_purge: "/audio/voice/purge.m4a",
  voice_health: "/audio/voice/health.m4a",
  voice_armor: "/audio/voice/armor.m4a",
  voice_grenade: "/audio/voice/grenade.m4a",
  voice_jetpack: "/audio/voice/jetpack.m4a",
  voice_rocketlauncher: "/audio/voice/rocketlauncher.m4a",
  voice_imperion: "/audio/voice/imperion.m4a",
  voice_antigravity: "/audio/voice/antigravity.m4a",
  voice_won: "/audio/voice/won.m4a",
  voice_lost: "/audio/voice/lost.m4a",
  voice_ghost: "/audio/voice/ghost.m4a",
  voice_overcharge: "/audio/voice/overcharge.m4a",
  voice_teamgame: "/audio/voice/teamgame.m4a",
  voice_sologame: "/audio/voice/sologame.m4a",
  voice_overdrive: "/audio/voice/overdrive.m4a",
  voice_musicchange: "/audio/voice/musicchange.m4a",
  voice_armageddonvoice: "/audio/voice/armageddonvoice.m4a",
  voice_gamestart: "/audio/voice/gamestart.m4a",
  voice_easy: "/audio/voice/easy.mp3",
  voice_medium: "/audio/voice/medium.mp3",
  voice_hard: "/audio/voice/hard.mp3",
  voice_veryeasy: "/audio/voice/veryeasy.mp3",
  voice_standard: "/audio/voice/standard.mp3",
  voice_moon: "/audio/voice/moon.mp3",
  voice_blitz: "/audio/voice/blitz.mp3",
  voice_glass_cannon: "/audio/voice/glass_cannon.mp3",
  voice_burnout: "/audio/voice/burnout.mp3",
  voice_flags: "/audio/voice/flags.mp3",
  voice_treasure: "/audio/voice/treasure.mp3",
  voice_treasure_loose: "/audio/voice/treasure_loose.mp3",
  voice_treasure_stolen: "/audio/voice/treasure_stolen.mp3",
  voice_flags_red_stole: "/audio/voice/flags_red_stole.mp3",
  voice_flags_blue_stole: "/audio/voice/flags_blue_stole.mp3",
  voice_flags_red_point: "/audio/voice/flags_red_point.mp3",
  voice_flags_blue_point: "/audio/voice/flags_blue_point.mp3",
  // 2026-07-23 batch: dedicated mode calls + loadout perks + event lines
  voice_relay: "/audio/voice/relay.mp3",
  voice_onegun: "/audio/voice/onegun.mp3",
  voice_horde: "/audio/voice/horde.mp3",
  voice_royale: "/audio/voice/royale.mp3",
  voice_sprinter: "/audio/voice/sprinter.mp3",
  voice_gunslinger: "/audio/voice/gunslinger.mp3",
  voice_vanguard: "/audio/voice/vanguard.mp3",
  voice_grenadier: "/audio/voice/grenadier.mp3",
  voice_body_double: "/audio/voice/body_double.mp3",
  voice_penisgun: "/audio/voice/penisgun.mp3",
  voice_watergun: "/audio/voice/watergun.mp3",
  voice_goblin_loot: "/audio/voice/goblin_loot.mp3",
  voice_rocket_chest: "/audio/voice/rocket_chest.mp3",
  voice_gun_taken: "/audio/voice/gun_taken.mp3",
  voice_gun_swap: "/audio/voice/gun_swap.mp3",
  voice_skull_taken: "/audio/voice/skull_taken.mp3",
  voice_skull_loose: "/audio/voice/skull_loose.mp3",
  // recorded map-name announcements (keyed by map id → voice_map_<id>)
  voice_map_grid_arena: "/audio/voice/map_grid_arena.mp3",
  voice_map_mega_chibre: "/audio/voice/map_mega_chibre.mp3",
  voice_map_compound: "/audio/voice/map_compound.mp3",
  voice_map_rooftops: "/audio/voice/map_rooftops.mp3",
  voice_map_rooftops_xl: "/audio/voice/map_rooftops_xl.mp3",
  voice_map_arena_xl: "/audio/voice/map_arena_xl.mp3",
  voice_map_village: "/audio/voice/map_village.mp3",
  voice_map_canyon: "/audio/voice/map_canyon.mp3",
  voice_map_sandlot: "/audio/voice/map_sandlot.mp3",
  voice_map_neo_island: "/audio/voice/map_neo_island.mp3",
  voice_map_medieval_castle: "/audio/voice/map_medieval_castle.mp3",
  voice_map_wild_west_train_pursuit: "/audio/voice/map_wild_west_train_pursuit.mp3",
  voice_map_neon_district: "/audio/voice/map_neon_district.mp3",
  voice_map_chibre_arena: "/audio/voice/map_chibre_arena.mp3",
  voice_map_plumbing_plaza: "/audio/voice/map_plumbing_plaza.mp3",
  voice_map_pirate_ship: "/audio/voice/map_pirate_ship.mp3",
  voice_map_rooftop_spiderweb: "/audio/voice/map_rooftop_spiderweb.mp3",
  voice_map_random: "/audio/voice/map_random.mp3",
  helicopter: "/audio/sfx/helicopter.mp3",
  uefo: "/audio/sfx/uefo.mp3",
  leaving: "/audio/voice/leaving.mp3",
  fireblast: "/audio/sfx/fireblast.wav",
  wormvomit: "/audio/sfx/wormvomit.wav",
  beam: "/audio/sfx/beam.wav",
  horseriding: "/audio/sfx/horseriding.mp3",
  bubble: "/audio/sfx/bubble.mp3",
  bossdamaged: "/audio/sfx/bossdamaged.mp3",
  // FART BLASTER hazard (2026-07-23): 7 recorded variations + the gag
  fart1: "/audio/sfx/fart1.wav",
  fart2: "/audio/sfx/fart2.wav",
  fart3: "/audio/sfx/fart3.wav",
  fart4: "/audio/sfx/fart4.wav",
  fart5: "/audio/sfx/fart5.wav",
  fart6: "/audio/sfx/fart6.wav",
  fart7: "/audio/sfx/fart7.wav",
  throwup: "/audio/sfx/throwup.wav",
  water: "/audio/sfx/water.mp3",
  voice_fartblaster: "/audio/voice/fartblaster.mp3",
  // PENIS GUN: recorded moans (from the photobooth project) — a random one
  // plays while the sperm hose fires.
  moan1: "/audio/sfx/moan1.wav",
  moan2: "/audio/sfx/moan2.wav",
  moan3: "/audio/sfx/moan3.wav",
  moan4: "/audio/sfx/moan4.wav",
  moan5: "/audio/sfx/moan5.wav",
} as const;

/** Number of recorded moans (moan1..moanN) for the penis gun. */
export const MOAN_COUNT = 5;

const OPTIONAL_SAMPLE_URLS: Record<string, readonly string[]> = {
  carbacktothefutur: [
    "/audio/sfx/carbacktothefutur.m4a",
    "/audio/sfx/carbacktothefutur.mp3",
    "/audio/sfx/carbacktothefutur.wav",
  ],
  voice_backtothefuture: [
    "/audio/voice/backtothefuture.m4a",
    "/audio/voice/backtothefuture.mp3",
    "/audio/voice/backtothefuture.wav",
    "/audio/voice/backtothefutur.m4a",
    "/audio/voice/backtothefutur.mp3",
    "/audio/voice/backtothefutur.wav",
  ],
};

/** Recorded NUMBER parts (voice announcer): 1–20, tens to 90, 100, 1000 —
 *  composed into any count ("round 23" = round + 20 + 3), plus the words
 *  round / bot / bots. Loaded outside SFX_URLS so they don't flood the
 *  settings mixer (they share one "voice_numbers" mixer entry instead). */
const NUMBER_SAMPLE_IDS = [
  ...Array.from({ length: 20 }, (_, i) => String(i + 1)),
  "30", "40", "50", "60", "70", "80", "90", "100", "1000",
  "round", "bot", "bots",
];

/** Composed number samples play 2 semitones down to match their recorded set. */
const NUM_PITCH_RATE = Math.pow(2, -2 / 12);
const LOWER_TODAY_VOICES = new Set([
  "standard", "moon", "blitz", "glass_cannon", "burnout", "flags",
  "treasure", "treasure_loose", "treasure_stolen",
  "flags_red_stole", "flags_blue_stole", "flags_red_point", "flags_blue_point",
  "map_rooftop_spiderweb",
  "relay", "onegun", "horde", "royale", "map_mega_chibre",
  "sprinter", "gunslinger", "vanguard",
  "grenadier", "body_double", "penisgun", "watergun", "goblin_loot", "rocket_chest",
  "gun_taken", "gun_swap", "skull_taken", "skull_loose", "fartblaster",
]);

export interface VehicleLoopSound {
  id: string;
  sample: "helicopter" | "uefo";
  x: number;
  y: number;
  z: number;
  /** Pitch shift in semitones. */
  semitone?: number;
  gain?: number;
  maxDistance?: number;
}

interface ActiveVehicleLoop {
  src: AudioScheduledSourceNode;
  osc?: OscillatorNode;
  gain: GainNode;
  panner: PannerNode;
  sample: string;
}

interface ActiveWaterLoop {
  src: AudioBufferSourceNode;
  gain: GainNode;
  panner: PannerNode;
}

type SinkableAudioContext = AudioContext & { setSinkId?: (sinkId: string) => Promise<void> };
type SinkableMedia = HTMLMediaElement & { setSinkId?: (sinkId: string) => Promise<void> };

export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain!: GainNode;
  private sfxGain!: GainNode;
  private musicGain!: GainNode;
  volumes = loadVolumes();
  /** Per-sound multipliers from the settings mixer (0..2, default 1). */
  soundVols = loadSoundVols();

  /** Whether the player WANTS their mic live — persisted so it carries from
   *  the lobby into the match and across rounds (voice chat auto-enables). */
  get micWanted(): boolean { try { return localStorage.getItem("neonpurge.micwanted") === "1"; } catch { return false; } }
  set micWanted(v: boolean) { try { localStorage.setItem("neonpurge.micwanted", v ? "1" : "0"); } catch { /* private mode */ } }

  constructor() {
    // account settings land in localStorage AFTER this module booted — the
    // login flow fires this event so the synced volumes apply immediately
    // (they only took effect after a manual reload before)
    window.addEventListener("np-settings-synced", () => {
      this.volumes = loadVolumes();
      this.soundVols = loadSoundVols();
      this.setVolumes({}); // re-applies gains to the live audio graph
    });

    // PHONE: leaving the tab (home screen, app switcher) must silence the
    // game completely — suspend the whole WebAudio graph and pause every
    // streamed track, then restore exactly what was playing on return.
    if ((navigator.maxTouchPoints ?? 0) > 0 && window.matchMedia("(pointer: coarse)").matches) {
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          void this.ctx?.suspend();
          this.bgPaused.length = 0;
          for (const el of [this.musicEl, this.dragonEl, this.duelEl, this.cinematicEl]) {
            if (el && !el.paused) { el.pause(); this.bgPaused.push(el); }
          }
        } else {
          this.preferPhoneOutput();
          this.ctx?.resume?.().catch(() => { /* iOS: audio device busy / needs a gesture — retried on the next tap */ });
          for (const el of this.bgPaused) void el.play().catch(() => { /* needs a gesture — onGesture retries */ });
          this.bgPaused.length = 0;
        }
      });
    }
    window.addEventListener("gamepadconnected", () => this.preferPhoneOutput());
    window.addEventListener("gamepaddisconnected", () => this.preferPhoneOutput());
  }
  /** Tracks paused because the tab went hidden (phone) — resumed on return. */
  private bgPaused: HTMLAudioElement[] = [];
  private noiseBuffer: AudioBuffer | null = null;
  private sampleBuffers = new Map<string, AudioBuffer>();
  private vehicleLoops = new Map<string, ActiveVehicleLoop>();
  private waterLoops = new Map<string, ActiveWaterLoop>();
  private musicEl: HTMLAudioElement | null = null;
  private musicSource: MediaElementAudioSourceNode | null = null;
  // DRAGON theme: crossfades with the map music by proximity to the dragon
  private dragonEl: HTMLAudioElement | null = null;
  private dragonSource: MediaElementAudioSourceNode | null = null;
  private dragonGain: GainNode | null = null;
  private dragonMix = 0;
  private dragonUrl = "";
  // DUEL theme: a second proximity layer that crossfades in near an enemy
  private duelEl: HTMLAudioElement | null = null;
  private duelSource: MediaElementAudioSourceNode | null = null;
  private duelGain: GainNode | null = null;
  private duelMix = 0;
  // HORSE riding: a looped sample while mounted
  private horseLoop: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private currentTrack: MusicTrack | null = null;
  /** True while a DeLorean jump is mid-flight: the map music is silenced so
   *  only the carbacktothefutur bed plays, until the new map's track starts. */
  private musicSuspended = false;
  // a themed track played during a cinematic (e.g. the map's own music pool)
  private cinematicEl: HTMLAudioElement | null = null;
  private cinematicSrc: MediaElementAudioSourceNode | null = null;
  private cinematicThemeActive = false;
  // spectrum analyser tapping the music bus (drives the boombox visualiser)
  private musicAnalyser: AnalyserNode | null = null;
  private analyserData: Uint8Array<ArrayBuffer> | null = null;
  private levelsBuf: number[] = []; // reused each frame — no per-frame array alloc
  private pendingTrack: MusicTrack | null = null;

  /** Must be called from a user gesture (browser autoplay policy). */
  resume(): void {
    this.onGesture();
  }

  /**
   * Call on every user gesture. Idempotent: resumes the context and ensures
   * the requested track is actually playing (autoplay may reject the first
   * attempt, so we keep retrying on subsequent gestures until it sticks).
   */
  onGesture(): void {
    if (!this.ctx) this.init();
    this.preferPhoneOutput();
    this.ctx!.resume().catch(() => { /* iOS: audio device busy / needs a gesture — retried on the next tap */ });
    if (this.pendingTrack) {
      const t = this.pendingTrack;
      this.pendingTrack = null;
      this.playMusic(t);
    } else if (this.musicEl && this.musicEl.paused && !this.musicSuspended) {
      void this.musicEl.play().catch(() => {
        if (this.currentTrack) this.pendingTrack = this.currentTrack;
      });
    }
  }

  private init(): void {
    const ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    this.ctx = ctx;
    this.masterGain = ctx.createGain();
    this.sfxGain = ctx.createGain();
    this.musicGain = ctx.createGain();
    this.sfxGain.connect(this.masterGain);
    this.musicGain.connect(this.masterGain);
    this.masterGain.connect(ctx.destination);
    this.applyVolumes();
    this.preferPhoneOutput();

    // shared white-noise buffer for procedural sounds (footsteps, chimes)
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;

    if (ctx.listener.forwardZ) ctx.listener.forwardZ.value = -1;

    // preload sampled SFX
    for (const [name, url] of Object.entries(SFX_URLS)) void this.loadSample(name, url);
    for (const [name, urls] of Object.entries(OPTIONAL_SAMPLE_URLS)) void this.loadOptionalSample(name, urls);
    // spoken number/round/bot parts for composed announcements
    for (const id of NUMBER_SAMPLE_IDS) void this.loadSample(`num_${id}`, `/audio/voice/number/${id}.mp3`);
  }

  /** Best-effort speaker guard for phones with controllers attached.
   *
   * Browsers do not expose "phone speaker" as a portable device id, and iOS
   * Safari exposes no output picker at all. Where supported, `""` asks the
   * browser to route media/WebAudio to its default output and clears stale
   * per-element sink choices after a controller connect/resume. */
  preferPhoneOutput(): void {
    const ctx = this.ctx as SinkableAudioContext | null;
    void ctx?.setSinkId?.("").catch(() => { /* unsupported or permission-limited */ });
    for (const el of this.mediaElements()) this.routeMediaElement(el);
  }

  private mediaElements(): HTMLAudioElement[] {
    return [this.musicEl, this.dragonEl, this.duelEl, this.cinematicEl].filter((el): el is HTMLAudioElement => !!el);
  }

  private routeMediaElement(el: HTMLMediaElement): void {
    void (el as SinkableMedia).setSinkId?.("").catch(() => { /* iOS/Firefox: no output API */ });
  }

  private async loadSample(name: string, url: string): Promise<void> {
    try {
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      const buf = await this.ctx!.decodeAudioData(arr);
      this.sampleBuffers.set(name, buf);
    } catch (err) {
      console.warn(`[audio] failed to load ${name}`, err);
    }
  }

  private async loadOptionalSample(name: string, urls: readonly string[]): Promise<void> {
    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const arr = await res.arrayBuffer();
        const buf = await this.ctx!.decodeAudioData(arr);
        this.sampleBuffers.set(name, buf);
        return;
      } catch {
        // Optional branded event audio may be absent in dev builds.
      }
    }
  }

  // ---------------------------------------------------------------- music

  /** Loop a music track through the music bus. Streams (memory-friendly).
   *  A "maps" request resolves to a random real map track so each match gets
   *  one of the two songs. */
  /** EASTER-EGG track: the dubstep armageddon anthem — plays ONCE at full tilt,
   *  then the map music resumes. */
  playArmageddon(): () => void {
    const prev = this.currentTrack;
    if (!this.ctx) return () => {};
    this.stopMusic();
    const el = new Audio("/audio/music/armageddon.mp3");
    el.loop = true; // the storm now outlasts one pass — loop until restored
    el.crossOrigin = "anonymous";
    this.routeMediaElement(el);
    this.musicEl = el;
    this.currentTrack = null;
    try { this.musicSource = this.ctx.createMediaElementSource(el); this.musicSource.connect(this.musicGain); }
    catch { el.volume = this.volumes.master * this.volumes.music; }
    // punch the music bus up for the drop (bus contract: musicGain carries
    // ONLY volumes.music — master is applied downstream by masterGain; the
    // old master* factor here double-applied it until a slider was touched)
    this.intensitySuspended = true; // the anthem owns the bus until restored
    this.musicGain.gain.setTargetAtTime(Math.min(1, this.volumes.music * 1.6 + 0.3), this.now(), 0.1);
    void el.play().catch(() => {});
    return () => {
      this.intensitySuspended = false;
      this.lastIntensity = -1; // force the next intensity write through
      this.musicGain.gain.setTargetAtTime(this.volumes.music, this.now(), 0.5);
      this.stopMusic();
      this.playMusic(prev && prev !== null ? prev : "maps");
    };
  }

  /**
   * DYNAMIC MUSIC: 0 = calm exploration, 1 = full firefight. Lifts the music
   * bus and nudges playback rate so the same track reads as more urgent —
   * no extra audio assets needed.
   *
   * Bus contract: musicGain carries ONLY volumes.music (master is applied
   * downstream by masterGain), so intensity multiplies THAT, never master.
   * Suppressed while the armageddon anthem owns the bus.
   */
  setMusicIntensity(x: number): void {
    const t = Math.max(0, Math.min(1, x));
    if (this.intensitySuspended || !this.ctx) return;
    if (Math.abs(t - this.lastIntensity) < 0.02) return; // don't spam the param
    this.lastIntensity = t;
    // gentle: 0.82x when calm, 1.28x at full tilt
    this.musicGain.gain.setTargetAtTime(this.volumes.music * (0.82 + 0.46 * t), this.now(), 0.7);
    // The +5% tempo lift resamples a STREAMING compressed <audio> in real time —
    // cheap on desktop, but on mobile Safari/Chrome it hitches the decoder every
    // time it moves, and this is driven ~per frame during a firefight (the exact
    // moment the main thread is busiest). Drop just the tempo bend on phones; the
    // gain swell above already carries the "heat" and runs on the audio graph.
    if (this.musicEl && !this.isMobile) this.musicEl.playbackRate = 1 + 0.05 * t;
  }
  private lastIntensity = -1;
  private intensitySuspended = false;
  /** Coarse-pointer touch device — see the visibilitychange guard in init(). */
  private readonly isMobile = (navigator.maxTouchPoints ?? 0) > 0 && window.matchMedia("(pointer: coarse)").matches;

  /** Force a specific map track NOW (music-chest switch), bypassing the
   *  "already playing maps, don't restart" guard. */
  forceTrack(track: MusicTrack): void {
    this.musicSuspended = false; // a fresh track ends any cinematic silence
    this.stopMusic();
    this.currentTrack = null;
    this.playMusic(track);
    if (this.ctx) this.musicGain.gain.setTargetAtTime(this.volumes.music, this.now(), 0.4);
  }

  /** Silence the map music (bus + element) for the DeLorean jump. Only the
   *  carbacktothefutur bed plays until the new map's track starts on arrival. */
  pauseMapMusic(): void {
    this.musicSuspended = true;
    if (this.musicEl) this.musicEl.pause();
    if (this.ctx) this.musicGain.gain.setTargetAtTime(0.0001, this.now(), 0.35);
  }

  /** Play a themed track over the map music for the duration of a cinematic.
   *  Pair with endCinematicTheme() to hand back to the map's normal track. */
  playCinematicTheme(url: string): void {
    this.stopCinematicTheme();
    this.stopMusic();
    this.musicSuspended = true;
    this.cinematicThemeActive = true;
    if (!this.ctx) return;
    const elm = new Audio(url);
    elm.loop = true; elm.crossOrigin = "anonymous";
    this.routeMediaElement(elm);
    this.cinematicEl = elm;
    try {
      this.cinematicSrc = this.ctx.createMediaElementSource(elm);
      this.cinematicSrc.connect(this.musicGain);
      this.musicGain.gain.setTargetAtTime(this.volumes.music, this.now(), 0.2);
    } catch { elm.volume = this.volumes.master * this.volumes.music; }
    void elm.play().catch(() => { /* autoplay-gated */ });
  }

  /** Stop the cinematic theme WITHOUT restarting the map music (cleanup). */
  stopCinematicTheme(): void {
    if (this.cinematicEl) { this.cinematicEl.pause(); this.cinematicEl.src = ""; }
    try { this.cinematicSrc?.disconnect(); } catch { /* fine */ }
    this.cinematicEl = null; this.cinematicSrc = null;
    this.cinematicThemeActive = false;
  }

  /** End the cinematic theme and start the map's normal track. No-op if none. */
  endCinematicTheme(mapId: string): void {
    if (!this.cinematicThemeActive) return;
    this.stopCinematicTheme();
    this.playMapMusic(mapId, true);
  }

  /** Start the in-match background track for a map: Western, Skyhaven and
   *  Medieval have their own pools; everything else uses the generic map set.
   *  `force` bypasses the "already playing in-match" guard — used on a DeLorean
   *  map swap so the NEW map's music actually replaces the old track. */
  playMapMusic(mapId: string, force = false): void {
    const track = this.pickMapTrack(mapId);
    if (force) this.forceTrack(track);
    else this.playMusic(track);
  }

  playCinematicMapTheme(mapId: string): void {
    this.playCinematicTheme(MUSIC_URLS[this.pickMapTrack(mapId)]);
  }

  private pickMapTrack(mapId: string): MusicTrack {
    const pool = MAP_MUSIC[mapId] ?? MAP_TRACKS;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /** `bars` normalised (0..1) music-band levels for a spectrum visualiser
   *  (the Skyhaven boombox). Taps the music bus, so it follows whatever track
   *  is playing. Returns zeros until the audio context exists. */
  getMusicLevels(bars: number): number[] {
    if (!this.ctx || !this.musicGain) {
      // reuse one zeroed buffer — this runs per frame on EQ-decor maps until
      // the first gesture creates the audio context
      const out = this.levelsBuf;
      out.length = bars;
      out.fill(0);
      return out;
    }
    if (!this.musicAnalyser) {
      const a = this.ctx.createAnalyser();
      a.fftSize = 64;
      a.smoothingTimeConstant = 0.8;
      this.musicGain.connect(a); // tap (analyser is a read-only sink)
      this.musicAnalyser = a;
      this.analyserData = new Uint8Array(a.frequencyBinCount);
    }
    const bins = this.analyserData!;
    this.musicAnalyser.getByteFrequencyData(bins);
    const usable = Math.floor(bins.length * 0.72); // low/mid bands carry the music
    const out = this.levelsBuf;
    out.length = bars;
    for (let i = 0; i < bars; i++) {
      const lo = Math.floor((i / bars) * usable);
      const hi = Math.max(lo + 1, Math.floor(((i + 1) / bars) * usable));
      let sum = 0;
      for (let j = lo; j < hi; j++) sum += bins[j];
      out[i] = Math.min(1, sum / (hi - lo) / 210);
    }
    return out;
  }

  playMusic(track: MusicTrack): void {
    if (track === "maps") track = MAP_TRACKS[Math.floor(Math.random() * MAP_TRACKS.length)];
    if (!this.ctx) {
      this.pendingTrack = track; // will start on the next resume() gesture
      return;
    }
    // any in-match track counts as "already playing maps" — don't restart mid-match
    if (this.currentTrack && IN_MATCH_TRACKS.has(this.currentTrack) && IN_MATCH_TRACKS.has(track)
        && this.musicEl && !this.musicEl.paused) return;
    if (this.currentTrack === track && this.musicEl && !this.musicEl.paused) return;
    this.stopMusic();
    const el = new Audio(MUSIC_URLS[track]);
    el.loop = true;
    el.crossOrigin = "anonymous";
    this.routeMediaElement(el);
    this.musicEl = el;
    this.currentTrack = track;
    try {
      this.musicSource = this.ctx.createMediaElementSource(el);
      this.musicSource.connect(this.musicGain);
    } catch {
      // some browsers dislike re-sourcing; fall back to element volume
      el.volume = this.volumes.master * this.volumes.music;
    }
    void el.play().catch(() => {
      // autoplay blocked — retry on next gesture
      this.pendingTrack = track;
    });
  }

  /** Duck the map music under whichever proximity layer (dragon / duel) is
   *  loudest right now, so the two overlays never fight over the bus. */
  private applyMusicDuck(): void {
    if (!this.ctx) return;
    const duck = Math.max(this.dragonMix, this.duelMix);
    this.musicGain.gain.setTargetAtTime(this.volumes.music * (1 - duck), this.now(), 0.25);
  }

  /** DRAGON PROXIMITY CROSSFADE: t=0 → pure map music, t=1 → pure dragon
   *  theme. `url` picks the theme (chinesedragon by default, medieval for the
   *  european dragon). Call every frame while a dragon is on the map. */
  setDragonProximity(tIn: number, url = "/audio/music/chinesedragon.m4a"): void {
    let t = Math.max(0, Math.min(1, tIn));
    // the armageddon anthem owns the bus (currentTrack null while its el plays)
    if (this.musicEl && this.currentTrack === null) t = 0;
    // a theme swap (different map/dragon) tears down the old element first
    if (this.dragonEl && this.dragonUrl !== url) this.stopDragonMusic();
    this.dragonMix = t;
    if (t > 0 && !this.dragonEl && this.ctx) {
      const el = new Audio(url);
      el.loop = true;
      el.crossOrigin = "anonymous";
      this.routeMediaElement(el);
      this.dragonEl = el;
      this.dragonUrl = url;
      try {
        this.dragonSource = this.ctx.createMediaElementSource(el);
        this.dragonGain = this.ctx.createGain();
        this.dragonGain.gain.value = 0;
        this.dragonSource.connect(this.dragonGain).connect(this.masterGain);
      } catch { el.volume = 0; }
      void el.play().catch(() => { /* autoplay-gated — retried below */ });
    }
    if (this.dragonEl && this.dragonEl.paused && t > 0) void this.dragonEl.play().catch(() => {});
    if (!this.ctx) return;
    this.applyMusicDuck();
    if (this.dragonGain) this.dragonGain.gain.setTargetAtTime(this.volumes.music * t * 1.15, this.now(), 0.25);
    else if (this.dragonEl) this.dragonEl.volume = Math.min(1, this.volumes.master * this.volumes.music * t);
  }

  /** Tear down the dragon theme (leaving the map / dragon slain). */
  stopDragonMusic(): void {
    if (this.dragonEl) { this.dragonEl.pause(); this.dragonEl.src = ""; }
    try { this.dragonSource?.disconnect(); this.dragonGain?.disconnect(); } catch { /* fine */ }
    this.dragonEl = null; this.dragonSource = null; this.dragonGain = null;
    this.dragonUrl = "";
    this.dragonMix = 0;
    this.applyMusicDuck();
  }

  /** DUEL PROXIMITY CROSSFADE: fades the western "duel" standoff song in over
   *  the map music as an enemy closes in (t=1) and back out as they leave. */
  setDuelProximity(tIn: number): void {
    let t = Math.max(0, Math.min(1, tIn));
    if (this.musicEl && this.currentTrack === null) t = 0; // armageddon owns the bus
    this.duelMix = t;
    if (t > 0 && !this.duelEl && this.ctx) {
      const el = new Audio("/audio/music/duelwest.mp3");
      el.loop = true;
      el.crossOrigin = "anonymous";
      this.routeMediaElement(el);
      this.duelEl = el;
      try {
        this.duelSource = this.ctx.createMediaElementSource(el);
        this.duelGain = this.ctx.createGain();
        this.duelGain.gain.value = 0;
        this.duelSource.connect(this.duelGain).connect(this.masterGain);
      } catch { el.volume = 0; }
      void el.play().catch(() => { /* autoplay-gated — retried below */ });
    }
    if (this.duelEl && this.duelEl.paused && t > 0) void this.duelEl.play().catch(() => {});
    if (!this.ctx) return;
    this.applyMusicDuck();
    if (this.duelGain) this.duelGain.gain.setTargetAtTime(this.volumes.music * t * 1.1, this.now(), 0.3);
    else if (this.duelEl) this.duelEl.volume = Math.min(1, this.volumes.master * this.volumes.music * t);
  }

  stopDuelMusic(): void {
    if (this.duelEl) { this.duelEl.pause(); this.duelEl.src = ""; }
    try { this.duelSource?.disconnect(); this.duelGain?.disconnect(); } catch { /* fine */ }
    this.duelEl = null; this.duelSource = null; this.duelGain = null;
    this.duelMix = 0;
    this.applyMusicDuck();
  }

  /** Loop the horse-hooves sample while mounted; fade + stop when dismounted. */
  setHorseRiding(on: boolean): void {
    if (on) {
      if (this.horseLoop || !this.ctx) return;
      const buf = this.sampleBuffers.get("horseriding");
      if (!buf) return; // sample not loaded yet
      const src = this.ctx.createBufferSource();
      src.buffer = buf; src.loop = true;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      src.connect(gain).connect(this.sfxGain);
      src.start();
      gain.gain.setTargetAtTime(this.soundVol("horseriding"), this.now(), 0.15);
      this.horseLoop = { src, gain };
    } else {
      const loop = this.horseLoop;
      if (!loop) return;
      this.horseLoop = null;
      loop.gain.gain.setTargetAtTime(0.0001, this.now(), 0.12);
      const src = loop.src;
      setTimeout(() => { try { src.stop(); src.disconnect(); loop.gain.disconnect(); } catch { /* done */ } }, 300);
    }
  }

  stopMusic(): void {
    this.stopDragonMusic();
    this.stopDuelMusic();
    if (this.musicEl) {
      this.musicEl.pause();
      this.musicEl.src = "";
    }
    try {
      this.musicSource?.disconnect();
    } catch {
      /* already disconnected */
    }
    this.musicEl = null;
    this.musicSource = null;
    this.currentTrack = null;
    // Tearing a track down ends any cinematic suspension: leaving mid-
    // DeLorean-jump otherwise left musicSuspended latched and the bus at
    // 0.0001 — the menu (and every later match) played into a muted bus.
    // playCinematicTheme re-latches right after its own stopMusic() call.
    this.musicSuspended = false;
    if (this.ctx) this.musicGain.gain.setTargetAtTime(this.volumes.music, this.now(), 0.4);
  }

  /** Every mixable sample name (sfx + voice lines), for the settings mixer.
   *  "voice_numbers" is the single shared level for all composed number/round/
   *  bot announcements. */
  sampleNames(): string[] {
    return [...Object.keys(SFX_URLS), ...Object.keys(OPTIONAL_SAMPLE_URLS), "voice_numbers"];
  }

  /** Effective per-sound level: the user's slider, else the calibrated default. */
  soundVol(name: string): number {
    return this.soundVols[name] ?? SOUND_DEFAULTS[name] ?? 1;
  }

  setSoundVol(name: string, v: number): void {
    this.soundVols[name] = Math.max(0, Math.min(2, v));
    try { localStorage.setItem(SVOL_KEY, JSON.stringify(this.soundVols)); } catch { /* fine */ }
  }

  resetSoundVols(): void {
    this.soundVols = {};
    try { localStorage.removeItem(SVOL_KEY); } catch { /* fine */ }
  }

  /** Fire a registered one-shot sample by name (positional if `at` is given).
   *  Returns false if the sample isn't loaded yet. */
  sfx(name: string, gainScale = 1, at?: [number, number, number]): boolean {
    return this.playSample(name, gainScale, at);
  }

  /** Settings mixer preview: play a sample at its adjusted volume. */
  preview(name: string): void {
    if (name === "voice_numbers") { this.voiceRound(7); return; }
    this.playSample(name, 1);
  }

  setVolumes(v: Partial<Volumes>): void {
    this.volumes = { ...this.volumes, ...v };
    localStorage.setItem(VOL_KEY, JSON.stringify(this.volumes));
    if (this.ctx) this.applyVolumes();
  }

  private applyVolumes(): void {
    this.masterGain.gain.value = this.volumes.master;
    this.sfxGain.gain.value = this.volumes.sfx;
    this.musicGain.gain.value = this.volumes.music;
    // fallback path (music element not routed through the graph)
    if (this.musicEl && !this.musicSource) {
      this.musicEl.volume = Math.min(1, this.volumes.master * this.volumes.music);
    }
  }

  /** Play a decoded sample through the sfx bus, optionally positional. */
  private playSample(name: string, gainScale = 1, at?: [number, number, number], rate = 1): boolean {
    const buf = this.sampleBuffers.get(name);
    if (!this.ctx || !buf) return false;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = gainScale * this.soundVol(name); // settings mixer / calibrated default
    if (at) {
      const p = this.panner(at[0], at[1], at[2]);
      src.connect(p).connect(g).connect(this.sfxGain);
    } else {
      src.connect(g).connect(this.sfxGain);
    }
    src.start(this.now());
    return true;
  }

  private playTimedSample(name: string, durationMs: number, gainScale = 1): boolean {
    const buf = this.sampleBuffers.get(name);
    if (!this.ctx || !buf) return false;
    const ctx = this.ctx;
    const t = this.now();
    const dur = Math.max(0.5, durationMs / 1000);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = Math.max(0.25, Math.min(4, buf.duration / dur));
    const g = ctx.createGain();
    g.gain.setValueAtTime(gainScale * this.soundVol(name), t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(g).connect(this.sfxGain);
    src.start(t);
    src.stop(t + dur + 0.03);
    return true;
  }

  /** Bomb fuse BEEP: a short positional blip that the caller fires faster and
   *  faster as the fuse burns down. `urgency` 0..1 raises pitch + volume. */
  bombBeep(at: [number, number, number], urgency: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = 1250 + urgency * 900; // rises as it's about to blow
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.264 + urgency * 0.264, t + 0.008); // +20% louder countdown
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.055);
    const p2 = this.panner(at[0], at[1], at[2]);
    osc.connect(p2).connect(g).connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + 0.07);
  }

  /** Master switch for the recorded announcer (settings toggle). */
  voiceEnabled = true;
  private voiceSrc: AudioBufferSourceNode | null = null;
  /** A force line (armageddon) can't be talked over until this wall-clock. */
  private voicePriorityUntil = 0;

  /** NEWEST WINS: every line cuts the one still talking and plays instantly —
   *  announcements are always reactive, never stale/queued. `force` lines
   *  (armageddon) additionally hold priority: nothing can cut THEM. */
  /** Scheduled parts of a composed line ("round twelve") — stopped as one. */
  private voiceSeq: AudioBufferSourceNode[] = [];

  private stopVoice(): void {
    try { this.voiceSrc?.stop(); } catch { /* already ended */ }
    this.voiceSrc = null;
    for (const s of this.voiceSeq) { try { s.stop(); } catch { /* already ended */ } }
    this.voiceSeq = [];
  }

  /** Fired after a recorded announcer line STARTS playing (subtitles). */
  onVoice: ((name: string) => void) | null = null;

  voice(name: string, opts?: { force?: boolean; gain?: number; interrupt?: boolean; semitone?: number }): void {
    if (!this.voiceEnabled) return;
    const now = Date.now();
    if (!opts?.force && now < this.voicePriorityUntil) return; // armageddon owns the mic
    // sologame reads quieter than the rest — nudge it up 10%
    const gain = (opts?.gain ?? (name === "sologame" ? 1.01 : 0.92)) * this.volumes.voice; // announcer bus slider
    // check the sample EXISTS before interrupting: a line whose asset failed
    // to load must not cut the current announcement and then play nothing
    const buf = this.sampleBuffers.get(`voice_${name}`);
    if (!this.ctx || !buf) return;
    this.stopVoice();
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = Math.pow(2, (opts?.semitone ?? (LOWER_TODAY_VOICES.has(name) ? -2 : 0)) / 12);
    const g = this.ctx.createGain();
    g.gain.value = gain * this.soundVol(`voice_${name}`);
    const send = this.ctx.createGain();
    send.gain.value = (LOWER_TODAY_VOICES.has(name) ? 0.28 : 0.18) * gain;
    src.connect(g).connect(this.sfxGain);
    src.connect(send).connect(this.reverb());
    src.start(this.now());
    this.voiceSrc = src;
    this.onVoice?.(name); // subtitles (match.ts → onboarding.ts)
    if (opts?.force) this.voicePriorityUntil = now + Math.min(5000, buf.duration * 1000 + 150);
  }

  /** Speak arbitrary text with the browser's TTS — used for things with no
   *  recorded line (every map name, the V.Easy difficulty). Honors the
   *  announcer on/off + volume, and cancels any in-flight speech so rapid
   *  selection (arrowing through maps) always reads the latest pick. */
  speak(text: string): void {
    if (!this.voiceEnabled) return;
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const clean = text.replace(/[^\p{L}\p{N}\s'&()-]/gu, "").replace(/\s+/g, " ").trim();
    if (!clean) return;
    try {
      const u = new SpeechSynthesisUtterance(clean);
      u.rate = 1.02;
      u.pitch = 0.9;
      u.volume = Math.min(1, 0.95 * this.volumes.voice);
      // prefer a stable English voice so it doesn't drift by locale
      const v = window.speechSynthesis.getVoices();
      const en = v.find((x) => /^en[-_]?(US|GB)?/i.test(x.lang)) ?? v.find((x) => /^en/i.test(x.lang));
      if (en) u.voice = en;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { /* speech synthesis is optional */ }
  }

  /** Is a recorded voice line loaded for this key? */
  hasVoice(name: string): boolean {
    return this.sampleBuffers.has(`voice_${name}`);
  }

  /** Announce a map by id: the recorded line if we have one, otherwise the
   *  browser TTS fallback so any new map still reads. */
  announceMap(mapId: string, displayName: string): void {
    const key = `map_${mapId === "__random__" ? "random" : mapId}`;
    if (this.hasVoice(key)) this.voice(key, { interrupt: true });
    else this.speak(mapId === "__random__" ? "Random map" : displayName);
  }

  /** Decompose a count into the recorded number samples (1–20, tens to 90,
   *  100, 1000): 23 → [20, 3]; 347 → [3, 100, 40, 7]; 2500 → [2, 1000, 5, 100]. */
  private numberParts(n: number): string[] {
    let v = Math.floor(n);
    if (!Number.isFinite(v) || v <= 0) return [];
    const parts: string[] = [];
    if (v >= 1000) { const k = Math.floor(v / 1000); if (k > 1) parts.push(...this.numberParts(k)); parts.push("1000"); v %= 1000; }
    if (v >= 100) { const h = Math.floor(v / 100); if (h > 1) parts.push(...this.numberParts(h)); parts.push("100"); v %= 100; }
    if (v > 20) { const t = Math.floor(v / 10) * 10; parts.push(String(t)); v -= t; }
    if (v > 0) parts.push(String(v));
    return parts;
  }

  /** Remove recorded head/tail room tone when composing number words. This
   * keeps 26 ordered as TWENTY then SIX while their reverb tails may overlap.
   * Cached per buffer: the full-sample scan (~100k samples/word) ran on EVERY
   * word of EVERY announcer line — decoded buffers never change, so scan once. */
  private audibleCache = new WeakMap<AudioBuffer, { start: number; duration: number }>();
  private audibleBounds(buf: AudioBuffer): { start: number; duration: number } {
    const hit = this.audibleCache.get(buf);
    if (hit) return hit;
    let peak = 0;
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]!));
    }
    const threshold = Math.max(0.004, peak * 0.025);
    let first = buf.length, last = -1;
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < data.length; i++) if (Math.abs(data[i]!) >= threshold) { first = Math.min(first, i); break; }
      for (let i = data.length - 1; i >= 0; i--) if (Math.abs(data[i]!) >= threshold) { last = Math.max(last, i); break; }
    }
    let out: { start: number; duration: number };
    if (last < first) out = { start: 0, duration: buf.duration };
    else {
      const pad = Math.floor(buf.sampleRate * 0.015);
      const a = Math.max(0, first - pad), b = Math.min(buf.length, last + pad);
      out = { start: a / buf.sampleRate, duration: Math.max(0.08, (b - a) / buf.sampleRate) };
    }
    this.audibleCache.set(buf, out);
    return out;
  }

  /** Speak recorded parts back-to-back as ONE announcer line (newest wins,
   *  same rules as voice()). Part ids are NUMBER_SAMPLE_IDS entries. */
  voiceParts(ids: string[], opts?: { gain?: number }): void {
    if (!this.voiceEnabled || !this.ctx || !ids.length) return;
    if (Date.now() < this.voicePriorityUntil) return; // armageddon owns the mic
    this.stopVoice();
    const gain = (opts?.gain ?? 0.95) * this.volumes.voice * this.soundVol("voice_numbers");
    let t = this.now();
    for (const id of ids) {
      const buf = this.sampleBuffers.get(`num_${id}`);
      if (!buf) continue; // not loaded yet — skip the word, keep the rest
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = NUM_PITCH_RATE; // -2 semitones (stretches duration too)
      const g = this.ctx.createGain();
      g.gain.value = gain;
      const send = this.ctx.createGain();
      send.gain.value = 0.18 * gain;
      src.connect(g).connect(this.sfxGain);
      src.connect(send).connect(this.reverb());
      const audible = this.audibleBounds(buf);
      src.start(t, audible.start, audible.duration);
      t += audible.duration / NUM_PITCH_RATE + 0.035;
      this.voiceSeq.push(src);
    }
  }

  /** Announcer: "ROUND <n>" (purge waves etc.). */
  voiceRound(n: number): void {
    this.voiceParts(["round", ...this.numberParts(n)]);
  }

  /** Announcer: "<n> BOT/BOTS" (lobby bot-count picker). Silent for 0. */
  voiceBots(n: number): void {
    if (n <= 0) return;
    this.voiceParts([...this.numberParts(n), n === 1 ? "bot" : "bots"]);
  }

  /** Announcer: a bare composed number. */
  voiceNumber(n: number): void {
    this.voiceParts(this.numberParts(n));
  }

  private reverbIR: ConvolverNode | null = null;
  /** Lazily-built reverb impulse (2.2 s exponential-decay noise tail), wired to
   *  the sfx bus through ONE persistent wet-return gain. Callers route their
   *  wet SEND into the convolver's input — they must NEVER connect its output,
   *  or the return fans out per call and the reverb piles up louder each time
   *  (the "leaving sting got weird reverb" bug). */
  private reverb(): ConvolverNode {
    if (this.reverbIR) return this.reverbIR;
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * 2.2);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
    }
    const c = ctx.createConvolver();
    c.buffer = buf;
    const wetOut = ctx.createGain();
    wetOut.gain.value = 1;
    c.connect(wetOut).connect(this.sfxGain); // the ONLY output connection
    this.reverbIR = c;
    return c;
  }

  /** Leaving-a-match sting: the recorded clip pitched 3 semitones DOWN with a
   *  wet reverb tail (dry to the bus, a send into the shared reverb). */
  leaving(): void {
    if (!this.ctx) { this.onGesture(); if (!this.ctx) return; }
    const buf = this.sampleBuffers.get("leaving");
    if (!buf) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = Math.pow(2, -3 / 12); // −3 semitones
    const vol = this.soundVol("leaving");
    const dry = ctx.createGain(); dry.gain.value = 0.85 * vol;
    const send = ctx.createGain(); send.gain.value = 0.6 * vol;
    src.connect(dry).connect(this.sfxGain);
    src.connect(send).connect(this.reverb()); // into the reverb INPUT, not its output
    src.start(this.now());
  }

  /** Play a sample at a RANDOM pitch (+1 .. −4 semitones) through the reverb —
   *  the dragon fireblast and the grav-beam launch. Positional if `at` given. */
  blast(name: string, at?: [number, number, number]): void {
    if (!this.ctx) return;
    const buf = this.sampleBuffers.get(name);
    if (!buf) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = Math.pow(2, (1 - Math.random() * 5) / 12); // +1 .. −4 semi
    const vol = this.soundVol(name);
    const dry = ctx.createGain(); dry.gain.value = 0.9 * vol;
    const send = ctx.createGain(); send.gain.value = 0.5 * vol;
    dry.connect(this.sfxGain);
    send.connect(this.reverb()); // into the reverb INPUT (shared return handles output)
    if (at) {
      const p = this.panner(at[0], at[1], at[2]);
      src.connect(p);
      p.connect(dry);
      p.connect(send);
    } else {
      src.connect(dry);
      src.connect(send);
    }
    src.start(this.now());
  }

  /** Sandworm vomit: play the recorded wet sample dry/positional, not as a boom. */
  wormVomit(at?: [number, number, number]): void {
    const rate = Math.pow(2, (-2.5 + Math.random() * 1.2) / 12);
    this.playSample("wormvomit", 1.18, at, rate);
  }

  /** PENIS GUN: a random recorded moan, positional, with a little pitch jitter. */
  moan(at?: [number, number, number]): void {
    const n = 1 + Math.floor(Math.random() * MOAN_COUNT);
    this.playSample(`moan${n}`, 1.0, at, 0.96 + Math.random() * 0.08);
  }

  /** A random TAUNT line — positional so nearby players hear you gloat. */
  taunt(at?: [number, number, number], kind?: number): void {
    // key the clip to the server's tauntKind so the taunter and every nearby
    // listener hear the SAME jab; fall back to random when kind is absent
    const n = kind === undefined ? 1 + Math.floor(Math.random() * 4) : (kind % 4) + 1;
    this.playSample(`taunt${n}`, 1.0, at);
  }
  /** 'Go away' — a driver just got booted from their vehicle (positional). */
  goaway(at?: [number, number, number]): void {
    this.playSample("goaway", 1.0, at);
  }

  /** DRAGON ROAR — layered saw sweep + noise growl (synthesized, positional).
   *  dying=true pitches the sweep DOWN into a death bellow. */
  dragonRoar(at?: [number, number, number], dying = false): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const dur = dying ? 1.4 : 1.0;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t);
    master.gain.exponentialRampToValueAtTime(0.85, t + 0.06);
    master.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    if (at) master.connect(this.panner(at[0], at[1], at[2])).connect(this.sfxGain);
    else master.connect(this.sfxGain);
    // twin detuned saws — the throat
    for (const det of [0, 7]) {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      const f0 = 90 + det, f1 = dying ? 45 : 150 + det;
      osc.frequency.setValueAtTime(f0, t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t + dur * 0.7);
      const g = ctx.createGain(); g.gain.value = 0.45;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass";
      lp.frequency.setValueAtTime(900, t);
      lp.frequency.exponentialRampToValueAtTime(dying ? 250 : 1600, t + dur * 0.5);
      osc.connect(lp).connect(g).connect(master);
      osc.start(t); osc.stop(t + dur);
    }
    // noise growl layer
    if (this.noiseBuffer) {
      const n = ctx.createBufferSource(); n.buffer = this.noiseBuffer; n.loop = true;
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 320; bp.Q.value = 1.2;
      const g = ctx.createGain(); g.gain.value = 0.5;
      n.connect(bp).connect(g).connect(master);
      n.start(t); n.stop(t + dur);
    }
  }

  /** PURGE containment field dropping — a descending sci-fi power-down sweep
   *  (positional). Synthesized so it needs no asset. */
  forcefield(at?: [number, number, number]): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(720, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.55);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.5, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(2600, t);
    lp.frequency.exponentialRampToValueAtTime(400, t + 0.55);
    osc.connect(lp).connect(g);
    if (at) g.connect(this.panner(at[0], at[1], at[2])).connect(this.sfxGain);
    else g.connect(this.sfxGain);
    osc.start(t); osc.stop(t + 0.62);
  }

  /** PURGE: a positional zombie growl/snarl (recorded), random voice + pitch. */
  zombieGrowl(at: [number, number, number]): void {
    const n = 1 + Math.floor(Math.random() * 3);
    this.playSample(`zombie${n}`, 0.85, at, 0.92 + Math.random() * 0.16);
  }

  /** PURGE round-start jingle: three rising ominous notes. */
  roundJingle(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t0 = this.now();
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = [98, 123, 147][i];
      const g = ctx.createGain();
      const t = t0 + i * 0.22;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.3, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      osc.connect(g).connect(this.sfxGain);
      osc.start(t); osc.stop(t + 0.55);
    }
  }

  private groanSrc: AudioScheduledSourceNode | null = null;
  private groanGain: GainNode | null = null;
  /** PURGE ambience: distant groans — volume scales with the living horde. */
  setGroans(level: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    if (level > 0 && !this.groanSrc && this.noiseBuffer) {
      const n = ctx.createBufferSource();
      n.buffer = this.noiseBuffer; n.loop = true;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass"; lp.frequency.value = 160; lp.Q.value = 3;
      const wob = ctx.createOscillator(); wob.frequency.value = 0.6;
      const wobG = ctx.createGain(); wobG.gain.value = 70;
      wob.connect(wobG).connect(lp.frequency); wob.start();
      const g = ctx.createGain(); g.gain.value = 0;
      n.connect(lp).connect(g).connect(this.sfxGain);
      n.start(this.now());
      this.groanSrc = n; this.groanGain = g;
    }
    if (this.groanGain) this.groanGain.gain.setTargetAtTime(Math.min(1, level) * 0.4, this.now(), 0.4);
    if (level <= 0 && this.groanSrc) {
      const src = this.groanSrc;
      setTimeout(() => { try { src.stop(); src.disconnect(); } catch { /* gone */ } }, 900);
      this.groanSrc = null; this.groanGain = null;
    }
  }

  private driftSrc: AudioScheduledSourceNode | null = null;
  private driftGain: GainNode | null = null;
  /** Arena drift (dérapage) screech — starts/stops with the drift state.
   *  Uses the recorded drifting sample when available, else filtered noise. */
  setDrifting(on: boolean): void {
    if (!this.ctx) return;
    if (on && !this.driftSrc) {
      const ctx = this.ctx;
      const buf = this.sampleBuffers.get("drifting");
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, this.now());
      g.gain.exponentialRampToValueAtTime(0.5, this.now() + 0.08);
      let src: AudioScheduledSourceNode;
      if (buf) {
        const b = ctx.createBufferSource();
        b.buffer = buf; b.loop = true;
        src = b;
        b.connect(g).connect(this.sfxGain);
      } else if (this.noiseBuffer) {
        // synthesized tire screech: looped noise through a squealing bandpass
        const n = ctx.createBufferSource();
        n.buffer = this.noiseBuffer; n.loop = true;
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass"; bp.frequency.value = 950; bp.Q.value = 6;
        const wob = ctx.createOscillator(); wob.frequency.value = 11;
        const wobG = ctx.createGain(); wobG.gain.value = 220;
        wob.connect(wobG).connect(bp.frequency); wob.start();
        n.connect(bp).connect(g).connect(this.sfxGain);
        src = n;
      } else return;
      src.start(this.now());
      this.driftSrc = src; this.driftGain = g;
    } else if (!on && this.driftSrc) {
      const t = this.now();
      this.driftGain?.gain.setTargetAtTime(0.0001, t, 0.06);
      const src = this.driftSrc;
      setTimeout(() => { try { src.stop(); src.disconnect(); } catch { /* gone */ } }, 250);
      this.driftSrc = null; this.driftGain = null;
    }
  }

  private windSrc: AudioBufferSourceNode | null = null;
  /** Loop the wind while jetpack-gliding (started once, stopped on land). */
  startWind(): void {
    if (this.windSrc || !this.ctx) return;
    const buf = this.sampleBuffers.get("wind");
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf; src.loop = true;
    const g = this.ctx.createGain(); g.gain.value = 0.55;
    src.connect(g).connect(this.sfxGain);
    src.start(this.now());
    this.windSrc = src;
  }
  stopWind(): void {
    try { this.windSrc?.stop(); } catch { /* already stopped */ }
    this.windSrc = null;
  }

  /** Update the listener to the local player's head + facing. */
  setListener(x: number, y: number, z: number, yaw: number): void {
    if (!this.ctx) return;
    const l = this.ctx.listener;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    if (l.positionX) {
      l.positionX.value = x;
      l.positionY.value = y;
      l.positionZ.value = z;
      l.forwardX.value = fx;
      l.forwardY.value = 0;
      l.forwardZ.value = fz;
      l.upX.value = 0;
      l.upY.value = 1;
      l.upZ.value = 0;
    } else {
      // Safari legacy
      (l as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
      (l as unknown as { setOrientation(...a: number[]): void }).setOrientation(fx, 0, fz, 0, 1, 0);
    }
  }

  private panner(x: number, y: number, z: number): PannerNode {
    const p = this.ctx!.createPanner();
    p.panningModel = "HRTF";
    p.distanceModel = "inverse";
    p.refDistance = 4;
    p.maxDistance = 60;
    p.rolloffFactor = 1;
    if (p.positionX) {
      p.positionX.value = x;
      p.positionY.value = y;
      p.positionZ.value = z;
    } else {
      (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
    }
    return p;
  }

  private setPannerPosition(p: PannerNode, x: number, y: number, z: number): void {
    if (p.positionX) {
      p.positionX.setTargetAtTime(x, this.now(), 0.035);
      p.positionY.setTargetAtTime(y, this.now(), 0.035);
      p.positionZ.setTargetAtTime(z, this.now(), 0.035);
    } else {
      (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
    }
  }

  updateVehicleLoops(items: VehicleLoopSound[]): void {
    if (!this.ctx) return;
    const live = new Set<string>();
    for (const item of items) {
      const buf = this.sampleBuffers.get(item.sample);
      live.add(item.id);
      let loop = this.vehicleLoops.get(item.id);
      // rebuild when the REAL sample finishes loading after an oscillator
      // fallback started — otherwise the placeholder hum plays forever
      const upgrade = !!loop?.osc && !!buf;
      if (!loop || loop.sample !== item.sample || upgrade) {
        if (loop) {
          try { loop.src.stop(); } catch { /* already stopped */ }
          try { loop.src.disconnect(); loop.panner.disconnect(); loop.gain.disconnect(); } catch { /* already disconnected */ }
        }
        let src: AudioScheduledSourceNode;
        let osc: OscillatorNode | undefined;
        if (buf) {
          const bsrc = this.ctx.createBufferSource();
          bsrc.buffer = buf;
          bsrc.loop = true;
          src = bsrc;
        } else {
          osc = this.ctx.createOscillator();
          osc.type = item.sample === "uefo" ? "sawtooth" : "triangle";
          src = osc;
        }
        const panner = this.ctx.createPanner();
        panner.panningModel = "equalpower";
        panner.distanceModel = "inverse";
        panner.refDistance = item.sample === "uefo" ? 12 : 8;
        panner.maxDistance = item.maxDistance ?? (item.sample === "uefo" ? 130 : 95);
        panner.rolloffFactor = item.sample === "uefo" ? 0.75 : 0.9;
        const gain = this.ctx.createGain();
        gain.gain.value = 0;
        src.connect(panner).connect(gain).connect(this.sfxGain);
        src.start(this.now());
        loop = { src, osc, panner, gain, sample: item.sample };
        this.vehicleLoops.set(item.id, loop);
      }
      const rate = Math.pow(2, (item.semitone ?? 0) / 12);
      const maybeRate = (loop.src as { playbackRate?: AudioParam }).playbackRate;
      maybeRate?.setTargetAtTime(rate, this.now(), 0.08);
      if (loop.osc) loop.osc.frequency.setTargetAtTime((item.sample === "uefo" ? 92 : 58) * rate, this.now(), 0.08);
      loop.gain.gain.setTargetAtTime((item.gain ?? (item.sample === "uefo" ? 0.75 : 0.62)) * this.soundVol(item.sample), this.now(), 0.08);
      this.setPannerPosition(loop.panner, item.x, item.y, item.z);
    }
    for (const [id, loop] of [...this.vehicleLoops]) {
      if (live.has(id)) continue;
      loop.gain.gain.setTargetAtTime(0.0001, this.now(), 0.06);
      const src = loop.src;
      window.setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, 180);
      this.vehicleLoops.delete(id);
    }
  }

  updateWaterLoops(items: { id: string; x: number; y: number; z: number; gain?: number }[]): void {
    if (!this.ctx) return;
    const buf = this.sampleBuffers.get("water");
    if (!buf) return;
    const live = new Set<string>();
    for (const item of items) {
      live.add(item.id);
      let loop = this.waterLoops.get(item.id);
      if (!loop) {
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        const panner = this.ctx.createPanner();
        panner.panningModel = "equalpower";
        panner.distanceModel = "inverse";
        panner.refDistance = 7;
        panner.maxDistance = 70;
        panner.rolloffFactor = 0.95;
        const gain = this.ctx.createGain();
        gain.gain.value = 0;
        src.connect(panner).connect(gain).connect(this.sfxGain);
        src.start(this.now());
        loop = { src, gain, panner };
        this.waterLoops.set(item.id, loop);
      }
      loop.gain.gain.setTargetAtTime((item.gain ?? 0.85) * this.soundVol("water"), this.now(), 0.05);
      this.setPannerPosition(loop.panner, item.x, item.y, item.z);
    }
    for (const [id, loop] of [...this.waterLoops]) {
      if (live.has(id)) continue;
      loop.gain.gain.setTargetAtTime(0.0001, this.now(), 0.06);
      const src = loop.src;
      window.setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, 180);
      this.waterLoops.delete(id);
    }
  }

  stopWaterLoops(): void {
    for (const loop of this.waterLoops.values()) {
      try { loop.src.stop(); } catch { /* already stopped */ }
      try { loop.src.disconnect(); loop.panner.disconnect(); loop.gain.disconnect(); } catch { /* already disconnected */ }
    }
    this.waterLoops.clear();
  }

  stopVehicleLoops(): void {
    for (const loop of this.vehicleLoops.values()) {
      try { loop.src.stop(); } catch { /* already stopped */ }
      try { loop.src.disconnect(); loop.panner.disconnect(); loop.gain.disconnect(); } catch { /* already disconnected */ }
    }
    this.vehicleLoops.clear();
  }

  private now(): number {
    return this.ctx!.currentTime;
  }

  /** Gunshot: real sample if loaded, else synthesized. Positional if given. */
  gunshot(pitch = 1, at?: [number, number, number]): void {
    if (!this.ctx) return;
    // real recorded gunshot takes priority
    if (this.playSample("gunshot", 0.5, at)) return;
    const ctx = this.ctx;
    const t = this.now();
    const dest = at ? this.panner(at[0], at[1], at[2]) : null;
    const out = ctx.createGain();
    out.gain.setValueAtTime(0.9, t);
    out.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    (dest ?? out).connect(dest ? out : this.sfxGain);
    if (dest) out.connect(this.sfxGain);

    // noise crack
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1800 * pitch;
    bp.Q.value = 0.7;
    noise.connect(bp).connect(out);
    noise.start(t);
    noise.stop(t + 0.18);

    // low thump
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(160 * pitch, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.1);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.5, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    osc.connect(og).connect(out);
    osc.start(t);
    osc.stop(t + 0.12);
  }

  private impOsc: OscillatorNode | null = null;
  private impGain: GainNode | null = null;

  /** IMPERION charge tell: rising pitch while t grows; call every frame with
   *  t = 0..1, and with t < 0 to stop. Audible to the shooter (remote
   *  players get a positional version via impChargeAt). */
  impCharge(t: number): void {
    if (!this.ctx) return;
    if (t < 0) {
      if (this.impOsc) { try { this.impOsc.stop(); } catch { /* already stopped */ } this.impOsc = null; this.impGain = null; }
      return;
    }
    if (!this.impOsc) {
      const ctx = this.ctx;
      this.impOsc = ctx.createOscillator();
      this.impOsc.type = "sawtooth";
      this.impGain = ctx.createGain();
      this.impGain.gain.value = 0.035;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass"; lp.frequency.value = 1200;
      this.impOsc.connect(lp); lp.connect(this.impGain); this.impGain.connect(this.sfxGain ?? ctx.destination);
      this.impOsc.start();
    }
    this.impOsc.frequency.setTargetAtTime(180 + 640 * t, this.now(), 0.03);
    if (this.impGain) this.impGain.gain.setTargetAtTime(0.03 + 0.05 * t, this.now(), 0.05);
  }

  /** IMPERION beam discharge — plays the recorded imperion shot LOUD, with a
   *  barely-attenuating panner so the whole map hears the thunderclap (still
   *  directional). Falls back to a synth sweep. `shotIdx` is which of the
   *  cell's charges fired: 1st normal pitch, 2nd a tone DOWN, 3rd a tone UP. */
  impShot(charge: number, at?: [number, number, number], shotIdx = 0, semis?: number): void {
    if (!this.ctx) return;
    const buf = this.sampleBuffers.get("imperion");
    if (buf) {
      const ctx = this.ctx;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const TONE = [1, 0.891, 1.122]; // 2^(±2/12): whole tone down / up
      // explicit semitone offset (dragon lance varies every blast) beats the table
      src.playbackRate.value = semis !== undefined ? Math.pow(2, semis / 12) : TONE[Math.max(0, Math.min(2, shotIdx))];
      const g = ctx.createGain();
      g.gain.value = 1.7; // LOUD (map-wide tell) — was 1.9, trimmed ~10% on feedback
      if (at) {
        // custom wide-range panner: direction without the usual falloff
        const p = ctx.createPanner();
        p.panningModel = "HRTF";
        p.distanceModel = "linear";
        p.refDistance = 40;
        p.maxDistance = 400; // barely fades across even the XL maps
        p.rolloffFactor = 0.35;
        if (p.positionX) { p.positionX.value = at[0]; p.positionY.value = at[1]; p.positionZ.value = at[2]; }
        else (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(at[0], at[1], at[2]);
        src.connect(p).connect(g).connect(this.sfxGain);
      } else {
        src.connect(g).connect(this.sfxGain);
      }
      src.start(this.now());
      return;
    }
    const ctx = this.ctx;
    const t = this.now();
    const osc = ctx.createOscillator();
    osc.type = "square";
    const fMul = semis !== undefined ? Math.pow(2, semis / 12) : 1;
    osc.frequency.setValueAtTime((900 + 600 * charge) * fMul, t);
    osc.frequency.exponentialRampToValueAtTime(140, t + 0.16 + 0.12 * charge);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.12 + 0.14 * charge, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2 + 0.15 * charge);
    osc.connect(g); g.connect(this.sfxGain ?? ctx.destination);
    osc.start(t); osc.stop(t + 0.4);
  }

  /** Knife swing: quick whoosh (optionally positional). */
  swing(at?: [number, number, number]): void {
    // real recorded knife swoosh
    if (this.playSample("knife", 1.1, at)) return;
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(600, t);
    bp.frequency.exponentialRampToValueAtTime(2400, t + 0.12);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.4, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
    noise.connect(bp).connect(g).connect(this.sfxGain);
    noise.start(t);
    noise.stop(t + 0.14);
  }

  /** Hitmarker tick / kill confirm. */
  hit(killed: boolean): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = killed ? 880 : 1500;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.25, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    osc.connect(g).connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + 0.08);
  }

  /** Pain sound when the local player takes damage. */
  /** Killed BY A KNIFE — the dedicated hit2 sting. */
  knifeDeath(): void {
    if (this.playSample("hurt2", 1)) return;
    this.playSample("die", 0.85); // fallback
  }

  /** Rocket/bomb LAUNCH (rocket launcher, kart turret, heli bomb, miniplane). */
  bombdrop(at?: [number, number, number]): void {
    if (this.playSample("bombdrop", 0.9, at)) return;
    this.gunshot(0.6, at); // fallback
  }

  hurt(): void {
    if (!this.ctx) return;
    if (this.playSample("hurt", 0.9)) return;
    // fallback: short low thud
    this.hit(false);
  }

  /** Death sound for the local player. */
  die(): void {
    if (!this.ctx) return;
    this.playSample("die", 0.85);
  }

  /** Reload: real sample if loaded, else synthesized clicks. */
  reload(): void {
    if (!this.ctx) return;
    if (this.playSample("reload", 0.8)) return;
    const ctx = this.ctx;
    const t = this.now();
    for (const [delay, freq] of [
      [0, 200],
      [0.15, 320],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = "square";
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0, t + delay);
      g.gain.linearRampToValueAtTime(0.15, t + delay + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + delay + 0.05);
      osc.connect(g).connect(this.sfxGain);
      osc.start(t + delay);
      osc.stop(t + delay + 0.06);
    }
  }

  /** Footstep tick (positional-agnostic; local player only). */
  footstep(pitch = 1, at?: [number, number, number]): void {
    // real recorded footstep, slightly pitch-varied so repeats don't feel robotic
    if (this.playSample("footstep", 1.35, at, pitch)) return;
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 400;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
    noise.connect(lp).connect(g).connect(this.sfxGain);
    noise.start(t);
    noise.stop(t + 0.07);
  }

  /** Plays when YOU kill an opponent. */
  killConfirm(): void {
    if (this.playSample("killConfirm", 1.1)) return;
    this.chime([880, 660], 0.08); // fallback
  }

  /** Explosion boom (rockets, vehicle destruction). Positional if given. */
  explosion(at?: [number, number, number]): void {
    if (this.playSample("explosion", 1.9, at)) return;
    this.gunshot(0.4, at); // fallback
  }

  /** DeLorean arrival beam: existing beam sample pitched down by semitones.
   *  Kept fairly quiet — it lands under the new map's music, not over it. */
  timeBeamArrival(semitone = -6): void {
    const rate = Math.pow(2, semitone / 12);
    if (this.playSample("beam", 0.55, undefined, rate)) return;
    this.promote();
  }

  /** DeLorean arrival hit: the imperion sample pitched down a few semitones —
   *  a deep "we've materialised" boom as the new map fades in. */
  imperionArrival(semitone = -5): void {
    const rate = Math.pow(2, semitone / 12);
    this.playSample("imperion", 1.0, undefined, rate);
  }

  /** DeLorean cinematic bed: generated so the feature works without a bundled
   *  licensed sample. The browser voice says the line when available. */
  backToFutureJump(durationMs = 6400): void {
    // MOBILE: the jump cutscene blocks input, so there's no gesture to wake a
    // context the OS auto-suspended — nudge it awake here or the whole cue
    // (voice + car bed) plays into a frozen graph and you hear nothing.
    if (!this.ctx) this.init();
    this.ctx?.resume?.().catch(() => { /* iOS: audio device busy / needs a gesture — retried on the next tap */ });
    if (this.sampleBuffers.has("voice_backtothefuture")) {
      this.voice("backtothefuture", { force: true, gain: 1.15 });
    } else if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        const u = new SpeechSynthesisUtterance("Back to the future.");
        u.rate = 0.92;
        u.pitch = 0.62;
        u.volume = Math.min(1, 0.92 * this.volumes.voice);
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
      } catch { /* speech synthesis is optional */ }
    }
    // play it FLAT at natural rate (the file is authored deliberately — no
    // stretch, no fade). The cinematic length is matched to the sample length.
    if (this.playSample("carbacktothefutur", 0.72)) return;
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = this.now();
    const dur = Math.max(2.5, durationMs / 1000);
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t);
    master.gain.exponentialRampToValueAtTime(0.48 * this.soundVol("beam"), t + 0.08);
    master.gain.setValueAtTime(0.48 * this.soundVol("beam"), t + Math.max(0.12, dur - 0.55));
    master.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    master.connect(this.sfxGain);

    const engine = ctx.createOscillator();
    engine.type = "sawtooth";
    engine.frequency.setValueAtTime(72, t);
    engine.frequency.exponentialRampToValueAtTime(132, t + dur * 0.48);
    engine.frequency.exponentialRampToValueAtTime(42, t + dur);
    const engineLp = ctx.createBiquadFilter();
    engineLp.type = "lowpass";
    engineLp.frequency.setValueAtTime(620, t);
    engineLp.frequency.exponentialRampToValueAtTime(1800, t + dur * 0.65);
    engine.connect(engineLp).connect(master);
    engine.start(t);
    engine.stop(t + dur);

    const flux = ctx.createOscillator();
    flux.type = "triangle";
    flux.frequency.setValueAtTime(330, t + 0.35);
    flux.frequency.exponentialRampToValueAtTime(1700, t + dur * 0.82);
    const fluxGain = ctx.createGain();
    fluxGain.gain.setValueAtTime(0.0001, t);
    fluxGain.gain.exponentialRampToValueAtTime(0.28, t + 0.45);
    fluxGain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    flux.connect(fluxGain).connect(master);
    flux.start(t + 0.2);
    flux.stop(t + dur);

    if (this.noiseBuffer) {
      const n = ctx.createBufferSource();
      n.buffer = this.noiseBuffer;
      n.loop = true;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.setValueAtTime(1400, t);
      hp.frequency.exponentialRampToValueAtTime(5200, t + dur);
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.0001, t);
      ng.gain.exponentialRampToValueAtTime(0.18, t + dur * 0.3);
      ng.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      n.connect(hp).connect(ng).connect(master);
      n.start(t);
      n.stop(t + dur);
    }
  }

  /** Promotion chime (rising). */
  promote(): void {
    this.chime([440, 660, 880], 0.09);
  }

  /** Demotion chime (falling). */
  demote(): void {
    this.chime([660, 440, 300], 0.09);
  }

  /** Easter-egg gore pop: a wet squishy burst (pitched-up hit sample). */
  gore(at?: [number, number, number]): void {
    if (this.playSample("hurt2", 0.75, at, 1.75)) return;
    this.playSample("hurt", 0.7, at, 1.6);
  }

  /** Stored health shot use: clean green-cross style rising pulse. */
  healShot(): void {
    this.chime([520, 780, 1040], 0.055);
  }

  /** Victory fanfare. */
  win(): void {
    this.chime([523, 659, 784, 1046], 0.12);
  }

  /** Level-up: a bright, quick rising arpeggio (distinct from the win fanfare). */
  levelUp(): void {
    this.chime([659, 880, 1109, 1319], 0.075);
  }

  /** Rank-up: the rarer, bigger celebration — a two-octave sweep with a topper. */
  rankUp(): void {
    this.chime([523, 659, 784, 1046, 1319, 1568], 0.085);
  }

  private chime(freqs: number[], step: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t0 = this.now();
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = f;
      const g = ctx.createGain();
      const t = t0 + i * step;
      g.gain.setValueAtTime(0.0, t);
      g.gain.linearRampToValueAtTime(0.22, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + step * 1.6);
      osc.connect(g).connect(this.sfxGain);
      osc.start(t);
      osc.stop(t + step * 1.8);
    });
  }

  dispose(): void {
    void this.ctx?.close();
    this.ctx = null;
  }
}

export const audio = new AudioManager();
